/**
 * RISK-FAMILIES-FILTER-PANEL.TSX
 * Filter family risks by population and places
 */

import useRiskFiltersSelection from "@/entities/physical-risk/hooks/useRiskFiltersSelection"
import ListItem from "@/components/list-item"
import FilterSidePanel from "@/components/FilterSidePanel"
import { PopulationFilterBlock, SiteFilterBlock } from "@/components/FilterBlock"
import PhysicalRisksSearch from "@/entities/physical-risk/ui/PhysicalRisksSearch"
import RiskSort from "@/entities/physical-risk/ui/RiskSort"
import { useAppSelector } from "@/core/root-reducer"
import populationStore from "@/entities/population/model/population.store"
import siteStore from "@/entities/site/model/site.store"
import riskFamilyStore from "@/entities/risk-family/model/risk-family.store"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import RisksUserSettings from "@/entities/risk-family/ui/RisksUserSettings"

export default function RiskFamiliesFilterPanel({ showDisplayMode = false }: { showDisplayMode?: boolean }) {
  const allPopulations = useAppSelector(populationStore.selectors.list)
  const allSites = useAppSelector(siteStore.selectors.list)
  const selectedSiteIds = useAppSelector(siteStore.selectors.selected)
  const selectedPopulationIds = useAppSelector(populationStore.selectors.selected)
  const isFamilyActive = useAppSelector(riskFamilyStore.selectors.isActive)
  const activeFamily = useAppSelector(riskFamilyStore.selectors.active)
  const allRisks = useAppSelector(physicalRiskStore.selectors.list)

  const familyRisks = isFamilyActive ? allRisks.filter((r) => r.RiskFamilyId === activeFamily.id) : null
  const familySiteIds = familyRisks ? new Set(familyRisks.flatMap((r) => r.siteIds)) : null
  const familyPopulationIds = familyRisks ? new Set(familyRisks.flatMap((r) => r.populationIds)) : null
  const selectedFilterCount = selectedSiteIds.length + selectedPopulationIds.length
  const showAdd = selectedFilterCount > 0
  const showRemove = selectedFilterCount > 1

  const sites = allSites.filter((x) => x.cRiskMax && (!familySiteIds || familySiteIds.has(x.id)))
  const populations = allPopulations.filter(
    (x) => x.cRiskMax && (!familyPopulationIds || familyPopulationIds.has(x.id))
  )
  const selectedSites = sites.filter((site) => selectedSiteIds.includes(site.id))
  const selectedPopulations = populations.filter((population) => selectedPopulationIds.includes(population.id))

  const { addPopulation, replaceWithSite, removeSite, replaceWithPopulation, removePopulation } =
    useRiskFiltersSelection()

  return (
    <FilterSidePanel width={400}>
      <ListItem>
        <PhysicalRisksSearch />
        <RiskSort store={physicalRiskStore} keys={{ risk: "cValueEffective", size: "cSize", name: "cName" }} />
        {showDisplayMode && <RisksUserSettings />}
      </ListItem>
      <SiteFilterBlock
        sites={sites}
        selectedSites={selectedSites}
        showAdd={showAdd}
        showRemove={showRemove}
        onReplace={replaceWithSite}
        onRemoveSite={removeSite}
      />
      <PopulationFilterBlock
        populations={populations}
        selectedPopulationIds={selectedPopulationIds}
        selectedPopulations={selectedPopulations}
        showAdd={showAdd}
        showRemove={showRemove}
        onAddPopulation={addPopulation}
        onReplacePopulation={replaceWithPopulation}
        onRemovePopulation={removePopulation}
      />
    </FilterSidePanel>
  )
}
